// GtStageEditor — Manage stages for one role.
//
// The stages are one ordered list, split by a line: above it the stages before
// the client sees anyone, below it the stages with the client. Moving a stage
// across the line changes its side. A stage with a client meaning (Client
// review, Offer, Placed, Rejected...) always sits with the client, so it cannot
// cross. The whole set is saved in one go through PUT /api/roles/:id/stages,
// which refuses to remove a stage that still has people in it.
import { useState, useEffect, useRef } from 'react';
import { GtSelect } from './GtControls';

const TONES = [
  { key: 'teal', hex: '#17838A' }, { key: 'slate', hex: '#64748B' }, { key: 'blue', hex: '#1565C0' },
  { key: 'purple', hex: '#6A1B9A' }, { key: 'amber', hex: '#B26B00' }, { key: 'green', hex: '#056F4E' }, { key: 'rose', hex: '#BE123C' },
];
const TONE_HEX = TONES.reduce((m, t) => { m[t.key] = t.hex; return m; }, {});

// What a stage means. 'work' and 'out' are stages of your own (no fixed meaning);
// the rest fix how the stage behaves and always sit with the client.
const KINDS = [
  { value: 'work', label: 'Working stage', help: 'People move through it' },
  { value: 'out', label: 'Way out', help: 'Asks for a reason, counts as out' },
  { value: 'client_review', label: 'Client review', fixed: true },
  { value: 'client_interested', label: 'Client interested', fixed: true },
  { value: 'client_interview', label: 'Client interview', fixed: true },
  { value: 'offer', label: 'Offer', fixed: true },
  { value: 'placed', label: 'Placed', fixed: true },
  { value: 'rejected', label: 'Rejected', fixed: true },
  { value: 'withdrawn', label: 'Withdrawn', fixed: true },
];
const FIXED = KINDS.filter((k) => k.fixed).map((k) => k.value);
const OUTCOME_OF = { placed: 'won', rejected: 'lost', withdrawn: 'lost', out: 'lost' };

function kindOf(s) {
  if (s.meaning && s.meaning !== 'none') return s.meaning;
  return s.outcome === 'lost' ? 'out' : 'work';
}
let seq = 0;
const uid = () => 'n' + (++seq);

export default function GtStageEditor({ roleId, roleTitle, authFetch, flash, onClose, onSaved }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [toneFor, setToneFor] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const focusNew = useRef(null);

  useEffect(() => {
    authFetch('/api/roles/' + roleId + '/stages').then((r) => {
      const st = (r.stages || []).slice().sort((a, b) => a.position - b.position);
      // Before the client first, then with the client, each in its saved order.
      const mk = (s) => ({ uid: uid(), id: s.id, key: s.key, label: s.label, tone: s.tone || 'slate', kind: kindOf(s),
        side: s.side === 'client' ? 'client' : 'internal', entry: !!s.is_entry, count: Number(s.candidate_count) || 0 });
      setRows(st.filter((s) => s.side !== 'client').map(mk).concat(st.filter((s) => s.side === 'client').map(mk)));
    }).catch((e) => { setErr(e.message); setRows([]); });
  }, [authFetch, roleId]);

  useEffect(() => {
    if (focusNew.current) {
      const el = document.getElementById('gtse-label-' + focusNew.current);
      if (el) el.focus();
      focusNew.current = null;
    }
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) tryClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function change(fn) { setRows((cur) => fn(cur.slice())); setDirty(true); setErr(null); }
  function patch(u, p) { change((cur) => cur.map((r) => (r.uid === u ? { ...r, ...p } : r))); }

  const internal = (rows || []).filter((r) => r.side === 'internal');
  const client = (rows || []).filter((r) => r.side === 'client');

  // Move one stage up or down. At the line it crosses sides instead of swapping,
  // unless its meaning keeps it with the client.
  function nudge(u, dir) {
    change((cur) => {
      const ins = cur.filter((r) => r.side === 'internal');
      const cls = cur.filter((r) => r.side === 'client');
      const row = cur.find((r) => r.uid === u);
      const list = row.side === 'internal' ? ins : cls;
      const i = list.indexOf(row);
      if (row.side === 'internal' && dir > 0 && i === list.length - 1) {
        return ins.filter((r) => r !== row).concat([{ ...row, side: 'client' }], cls);
      }
      if (row.side === 'client' && dir < 0 && i === 0) {
        if (FIXED.indexOf(row.kind) !== -1) return cur;
        return ins.concat([{ ...row, side: 'internal' }], cls.filter((r) => r !== row));
      }
      const j = i + dir;
      if (j < 0 || j >= list.length) return cur;
      const next = list.slice();
      next[i] = list[j]; next[j] = row;
      return row.side === 'internal' ? next.concat(cls) : ins.concat(next);
    });
  }

  // Drop a dragged stage before another, or at the end of a side.
  function dropAt(targetUid, side) {
    const u = drag;
    setDrag(null); setOver(null);
    if (!u || u === targetUid) return;
    change((cur) => {
      const row = cur.find((r) => r.uid === u);
      const toSide = targetUid ? cur.find((r) => r.uid === targetUid).side : side;
      if (toSide === 'internal' && FIXED.indexOf(row.kind) !== -1) return cur;
      const moved = { ...row, side: toSide };
      const rest = cur.filter((r) => r.uid !== u);
      let at;
      if (targetUid) at = rest.findIndex((r) => r.uid === targetUid);
      else at = toSide === 'internal' ? rest.filter((r) => r.side === 'internal').length : rest.length;
      rest.splice(at, 0, moved);
      return rest.filter((r) => r.side === 'internal').concat(rest.filter((r) => r.side === 'client'));
    });
  }

  function setKind(u, kind) {
    change((cur) => {
      const row = cur.find((r) => r.uid === u);
      let next = cur.map((r) => (r.uid === u ? { ...r, kind, entry: OUTCOME_OF[kind] ? false : r.entry } : r));
      // A client meaning moves the stage to the client side, at the top of it.
      if (FIXED.indexOf(kind) !== -1 && row.side === 'internal') {
        const moved = next.find((r) => r.uid === u);
        next = next.filter((r) => r.uid !== u);
        const ins = next.filter((r) => r.side === 'internal');
        const cls = next.filter((r) => r.side === 'client');
        next = ins.concat([{ ...moved, side: 'client' }], cls);
      }
      return next;
    });
  }

  function setEntry(u) { change((cur) => cur.map((r) => ({ ...r, entry: r.uid === u }))); }

  function add(side) {
    const u = uid();
    focusNew.current = u;
    change((cur) => {
      const fresh = { uid: u, id: null, key: null, label: '', tone: side === 'client' ? 'blue' : 'teal', kind: 'work', side, entry: false, count: 0 };
      const ins = cur.filter((r) => r.side === 'internal');
      const cls = cur.filter((r) => r.side === 'client');
      return side === 'internal' ? ins.concat([fresh], cls) : ins.concat(cls, [fresh]);
    });
  }

  function remove(u) { change((cur) => cur.filter((r) => r.uid !== u)); }

  // Plain checks before anything is sent.
  function problems() {
    const out = [];
    const all = rows || [];
    if (!all.length) out.push('A role needs at least one stage.');
    if (all.some((r) => !String(r.label).trim())) out.push('Every stage needs a name.');
    const seen = {};
    all.forEach((r) => {
      const k = String(r.label).trim().toLowerCase();
      if (k && seen[k]) out.push('Two stages are both called "' + String(r.label).trim() + '".');
      seen[k] = true;
    });
    const entries = all.filter((r) => r.entry);
    if (entries.length !== 1) out.push('Pick the one stage where new people land.');
    FIXED.forEach((m) => {
      const n = all.filter((r) => r.kind === m).length;
      if (n > 1) out.push('Only one stage can mean ' + KINDS.find((k) => k.value === m).label + '.');
    });
    return Array.from(new Set(out));
  }

  async function save() {
    const p = problems();
    if (p.length) { setErr(p[0]); return; }
    setBusy(true); setErr(null);
    try {
      const stages = rows.map((r) => {
        const fixed = FIXED.indexOf(r.kind) !== -1;
        return {
          id: r.id || undefined, label: String(r.label).trim(), tone: r.tone,
          meaning: fixed ? r.kind : 'none',
          outcome: OUTCOME_OF[r.kind] || 'open',
          side: fixed ? 'client' : r.side,
          is_entry: !!r.entry,
        };
      });
      await authFetch('/api/roles/' + roleId + '/stages', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stages }),
      });
      flash('Stages saved.');
      onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  function tryClose() {
    if (!dirty) { onClose(); return; }
    // An unsaved change is easy to lose by a stray click, so the scrim does nothing.
    setErr('You have changes that are not saved. Save them, or press Discard.');
  }

  function row(r, i, list) {
    const fixed = FIXED.indexOf(r.kind) !== -1;
    const isFirstClient = r.side === 'client' && i === 0;
    const isLastInternal = r.side === 'internal' && i === list.length - 1;
    const upBlocked = (i === 0 && r.side === 'internal') || (isFirstClient && fixed);
    const downBlocked = i === list.length - 1 && r.side === 'client';
    const usedFixed = (rows || []).filter((x) => x.uid !== r.uid && FIXED.indexOf(x.kind) !== -1).map((x) => x.kind);
    const kindOpts = KINDS.filter((k) => usedFixed.indexOf(k.value) === -1)
      .map((k) => ({ value: k.value, label: k.label }));
    const canEntry = !OUTCOME_OF[r.kind];
    return (
      <div key={r.uid}
        className={'gtse-row' + (drag === r.uid ? ' gtse-row--drag' : '') + (over === r.uid ? ' gtse-row--over' : '')}
        draggable
        onDragStart={(e) => { setDrag(r.uid); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', r.label || 'stage'); }}
        onDragEnd={() => { setDrag(null); setOver(null); }}
        onDragOver={(e) => { if (!drag) return; e.preventDefault(); if (over !== r.uid) setOver(r.uid); }}
        onDrop={(e) => { e.preventDefault(); dropAt(r.uid); }}>
        <span className="gtse-grip" aria-hidden="true" title="Drag to reorder">⠿</span>
        <div className="gtse-nudge">
          <button type="button" aria-label={'Move ' + (r.label || 'stage') + ' up'} disabled={upBlocked} onClick={() => nudge(r.uid, -1)}>▲</button>
          <button type="button" aria-label={'Move ' + (r.label || 'stage') + ' down'} disabled={downBlocked}
            title={isLastInternal ? 'Moves it to the client side' : undefined} onClick={() => nudge(r.uid, 1)}>▼</button>
        </div>
        <div className="gtse-tonewrap">
          <button type="button" className="gtse-tone" style={{ background: TONE_HEX[r.tone] || TONE_HEX.slate }}
            aria-label={'Colour for ' + (r.label || 'this stage')} onClick={() => setToneFor(toneFor === r.uid ? null : r.uid)} />
          {toneFor === r.uid && (
            <div className="gtse-tones" role="listbox" aria-label="Colours">
              {TONES.map((t) => (
                <button type="button" key={t.key} role="option" aria-selected={r.tone === t.key} aria-label={t.key}
                  className={'gtse-sw' + (r.tone === t.key ? ' gtse-sw--on' : '')} style={{ background: t.hex }}
                  onClick={() => { patch(r.uid, { tone: t.key }); setToneFor(null); }} />
              ))}
            </div>
          )}
        </div>
        <input id={'gtse-label-' + r.uid} className="gtse-label" value={r.label} maxLength={60} placeholder="Stage name"
          aria-label="Stage name" onChange={(e) => patch(r.uid, { label: e.target.value })} />
        <div className="gtse-kind">
          <GtSelect value={r.kind} options={kindOpts} onChange={(v) => setKind(r.uid, v)} />
        </div>
        <label className={'gtse-entry' + (r.entry ? ' gtse-entry--on' : '') + (canEntry ? '' : ' gtse-entry--off')}
          title={canEntry ? 'New people land here' : 'People cannot land in a way out or Placed'}>
          <input type="radio" name="gtse-entry" checked={r.entry} disabled={!canEntry} onChange={() => setEntry(r.uid)} />
          <span>{r.entry ? 'New people land here' : 'Land here'}</span>
        </label>
        <span className={'gtse-count' + (r.count ? '' : ' gtse-count--zero')} title={r.count ? r.count + ' on this stage now' : 'Nobody on this stage'}>{r.count}</span>
        <button type="button" className="gtse-del" disabled={r.count > 0}
          title={r.count > 0 ? 'Move the ' + r.count + (r.count === 1 ? ' person' : ' people') + ' out first' : 'Remove this stage'}
          aria-label={'Remove ' + (r.label || 'this stage')} onClick={() => remove(r.uid)}>×</button>
      </div>
    );
  }

  const issues = rows ? problems() : [];

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && tryClose()}>
      <div className="gtc-modal gtse-modal" role="dialog" aria-label="Manage stages" onClick={(e) => { e.stopPropagation(); if (toneFor) setToneFor(null); }}>
        <div className="gtc-modal-head gtse-head">
          <div>
            <div>Manage stages</div>
            {roleTitle && <div className="gtse-sub">{roleTitle}</div>}
          </div>
        </div>
        <div className="gtc-modal-body gtse-body">
          {rows === null ? <div className="gtr-empty-sm">Loading…</div> : (
            <>
              <p className="gtse-help">Rename, reorder, colour and add stages for this role only. Drag a stage across the line to change its side. A stage with people in it cannot be removed until they are moved.</p>
              <div className="gtse-cols" aria-hidden="true">
                <span /><span /><span /><span>Name</span><span>What it means</span><span>Entry</span><span>People</span><span />
              </div>

              <div className="gtse-section">
                <div className="gtse-sechead"><b>Before the client</b><span>Only you see these</span></div>
                <div className="gtse-list">{internal.map((r, i) => row(r, i, internal))}</div>
                <div className={'gtse-dropend' + (over === '__internal' ? ' gtse-dropend--over' : '')}
                  onDragOver={(e) => { if (!drag) return; e.preventDefault(); setOver('__internal'); }}
                  onDrop={(e) => { e.preventDefault(); dropAt(null, 'internal'); }}>
                  <button type="button" className="gtse-add" onClick={() => add('internal')}>+ Add a stage</button>
                </div>
              </div>

              <div className="gtse-line"><span>The client sees people from here down</span></div>

              <div className="gtse-section gtse-section--client">
                <div className="gtse-sechead"><b>With the client</b><span>Moving someone here changes what their portal shows</span></div>
                <div className="gtse-list">{client.map((r, i) => row(r, i, client))}</div>
                <div className={'gtse-dropend' + (over === '__client' ? ' gtse-dropend--over' : '')}
                  onDragOver={(e) => { if (!drag) return; e.preventDefault(); setOver('__client'); }}
                  onDrop={(e) => { e.preventDefault(); dropAt(null, 'client'); }}>
                  <button type="button" className="gtse-add" onClick={() => add('client')}>+ Add a stage</button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="gtc-modal-foot gtse-foot">
          <div className="gtse-msg" role="status">
            {err ? <span className="gtse-err">{err}</span>
              : dirty && issues.length ? <span className="gtse-warn">{issues[0]}</span>
                : dirty ? <span className="gtse-ok">Unsaved changes</span> : null}
          </div>
          {dirty
            ? <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Discard</button>
            : <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Close</button>}
          <button type="button" className="gtc-primary" disabled={busy || !dirty || !rows} onClick={save}>{busy ? 'Saving…' : 'Save stages'}</button>
        </div>
      </div>
    </div>
  );
}
